import { useEffect, useId, useRef, useState } from 'react'
import { CalendarOutlined, DownOutlined, IconArrowRight } from '../icons'

export interface DateRangePreset {
  readonly id: string
  readonly label: string
}

export interface DateRange {
  readonly from: Date
  readonly to: Date
}

export interface DateRangePickerProps {
  /** Accessible name for the popover (e.g. "Date range for spend summary"). */
  readonly label: string
  /** Left-to-right preset order (components.md convention: presets first,
   * calendar second — matches the design handoff's spend range-picker,
   * `Fictiv Platform.dc.html` ~L7803/~L3178). */
  readonly presets: readonly DateRangePreset[]
  /** The currently-active preset id, or `null` when the selection is a
   * custom (non-preset) calendar range. */
  readonly activePresetId: string | null
  /** The committed range; `null` means "all time" (no range constraint). */
  readonly value: DateRange | null
  /** Trigger button text, e.g. "Jul 20 – Aug 19" or a preset label. */
  readonly triggerLabel: string
  readonly onPresetSelect: (id: string) => void
  readonly onRangeSelect: (range: DateRange) => void
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function addMonths(date: Date, count: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + count, 1)
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

interface MonthCell {
  readonly date: Date
  readonly inMonth: boolean
  readonly isEndpoint: boolean
  readonly inRange: boolean
}

interface MonthModel {
  readonly title: string
  readonly cells: readonly MonthCell[]
}

function buildMonth(year: number, month: number, range: DateRange | null): MonthModel {
  const first = new Date(year, month, 1)
  const startDow = first.getDay()
  const cells: MonthCell[] = []
  for (let i = 0; i < 42; i++) {
    const date = new Date(year, month, 1 - startDow + i)
    const inMonth = date.getMonth() === month
    const time = date.getTime()
    const inRange = range ? time >= range.from.getTime() && time <= range.to.getTime() : false
    const isEndpoint = range ? isSameDay(date, range.from) || isSameDay(date, range.to) : false
    cells.push({ date, inMonth, isEndpoint, inRange })
  }
  return {
    title: first.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
    cells,
  }
}

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

/**
 * Two-month calendar range-picker + preset list (S4/O4, components.md
 * pattern shared with `Modal`/`Button`). Matches the design handoff's spend
 * date-range picker (`Fictiv Platform.dc.html` ~L3161-3204 / ~L7803-7818):
 * a trigger button opens a popover with a preset sidebar on the left and a
 * two-month calendar (prev/next month + prev/next year nav) on the right;
 * clicking a day starts a range, clicking a second day completes it.
 *
 * Accessibility (accessibility-wcag skill): the trigger is a real `<button>`
 * with `aria-haspopup`/`aria-expanded`; the popover is a non-modal but
 * focus-trapping labeled `dialog` (deliberate — see `close()`'s doc comment
 * for the trap itself). Focus returns to the trigger on `Escape` and on a
 * confirm/select action (preset click or completed calendar range) — NOT on
 * an outside pointer-click dismissal, where focus is left wherever the click
 * landed (WAI-ARIA APG: restore-on-close is a keyboard-dismissal/confirm
 * behavior, not a click-away one). Every preset and every in-month day is a
 * native `<button>`
 * (keyboard + screen-reader operable via Tab/Enter/Space, no custom roving
 * grid navigation needed to clear the WCAG 2.2 AA operability bar); adjacent
 * out-of-month day numbers are decorative (`aria-hidden`, not focusable) —
 * they exist only to keep the 6x7 grid visually square.
 */
export function DateRangePicker({
  label,
  presets,
  activePresetId,
  value,
  triggerLabel,
  onPresetSelect,
  onRangeSelect,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const [anchorMonth, setAnchorMonth] = useState(() => addMonths(value?.to ?? new Date(), -1))
  const [draftStart, setDraftStart] = useState<Date | null>(null)

  const dialogId = useId()
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return

    // Focus containment (WAI-ARIA APG dialog pattern): move focus INTO the
    // popover on open (the first preset button — the first focusable control
    // in reading order) and wrap Tab/Shift+Tab at the dialog's own boundary
    // below, so a keyboard user can never Tab out of a still-open dialog.
    const focusables = getFocusable()
    focusables[0]?.focus()

    function getFocusable(): HTMLElement[] {
      return Array.from(
        popoverRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      )
    }

    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      // Outside POINTER-click dismissal: close without forcing focus back to
      // the trigger — WAI-ARIA APG restores focus on keyboard-initiated
      // close (Escape) and on an explicit confirm/select action, not on a
      // click-away, so focus stays wherever the click landed.
      close({ restoreFocus: false })
    }

    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        close()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = getFocusable()
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  /**
   * `restoreFocus` defaults to `true` — keyboard dismissal (Escape) and a
   * confirm/select action (a preset click, or completing a calendar range)
   * both return focus to the trigger. An outside pointer-click dismissal
   * passes `false` (WAI-ARIA APG: restore on keyboard close, not click-away)
   * — see the `handlePointerDown` call site above.
   */
  function close({ restoreFocus = true }: { restoreFocus?: boolean } = {}) {
    setOpen(false)
    setDraftStart(null)
    if (restoreFocus) {
      triggerRef.current?.focus()
    }
  }

  function toggle() {
    if (!open) {
      setAnchorMonth(addMonths(value?.to ?? new Date(), -1))
      setDraftStart(null)
    }
    setOpen((current) => !current)
  }

  function handlePreset(id: string) {
    onPresetSelect(id)
    close()
  }

  function handleDayClick(date: Date) {
    const day = startOfDay(date)
    if (!draftStart) {
      setDraftStart(day)
      return
    }
    const from = day.getTime() <= draftStart.getTime() ? day : draftStart
    const to = day.getTime() <= draftStart.getTime() ? draftStart : day
    onRangeSelect({ from, to })
    close()
  }

  const effectiveRange: DateRange | null = draftStart ? { from: draftStart, to: draftStart } : value

  const leftMonth = buildMonth(anchorMonth.getFullYear(), anchorMonth.getMonth(), effectiveRange)
  const rightAnchor = addMonths(anchorMonth, 1)
  const rightMonth = buildMonth(rightAnchor.getFullYear(), rightAnchor.getMonth(), effectiveRange)

  const startLabel = value
    ? value.from.toLocaleDateString(undefined, {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })
    : '—'
  const endLabel = value
    ? value.to.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: 'numeric' })
    : '—'

  return (
    <div className="date-range-picker">
      <button
        type="button"
        ref={triggerRef}
        className="date-range-picker-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? dialogId : undefined}
        onClick={toggle}
      >
        <CalendarOutlined aria-hidden="true" className="icon-leading" />
        {triggerLabel}
        <DownOutlined aria-hidden="true" className="icon-trailing" />
      </button>

      {/* UID-317 review follow-up (mobile): a backdrop behind the bottom-sheet popover so it reads
       * as a dismissable modal — dims the page and closes on tap (it's outside the popover, so the
       * existing outside-pointer-down handler closes the picker). Hidden on desktop/tablet (CSS),
       * where the popover is an anchored dropdown that needs no scrim. `aria-hidden` — decorative;
       * Escape and outside-click already provide the accessible close. */}
      {open && <div className="date-range-picker-backdrop" aria-hidden="true" />}
      {open && (
        <div
          id={dialogId}
          ref={popoverRef}
          // biome-ignore lint/a11y/useSemanticElements: a non-modal, anchored
          // popover (no backdrop, custom outside-click/Escape close below) —
          // native <dialog> brings top-layer/backdrop semantics this doesn't
          // want; same hand-rolled-ARIA-dialog precedent as Modal.tsx.
          role="dialog"
          aria-label={label}
          className="date-range-picker-popover"
        >
          <div className="date-range-picker-header">
            <span className="date-range-picker-header-label">Date range</span>
            <span className="date-range-picker-header-value">{startLabel}</span>
            {/* UID-449 item 5: the design's separator is a stroked arrow with a
            shaft (`Fictiv Platform.dc.html:3778`), not Ant's filled caret —
            same rationale already recorded for `IconChevronRight`. */}
            <IconArrowRight />
            <span className="date-range-picker-header-value">{endLabel}</span>
          </div>

          <div className="date-range-picker-body">
            {/* biome-ignore lint/a11y/useSemanticElements: a <fieldset> would
            impose default browser chrome (border/legend spacing) this
            button-group doesn't want; role="group" + aria-label is a valid
            ARIA substitute (WAI-ARIA "group" role). */}
            <div className="date-range-picker-presets" role="group" aria-label="Presets">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className={
                    activePresetId === preset.id
                      ? 'date-range-picker-preset date-range-picker-preset-active'
                      : 'date-range-picker-preset'
                  }
                  aria-pressed={activePresetId === preset.id}
                  onClick={() => handlePreset(preset.id)}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="date-range-picker-calendar">
              {/* UID-449 item 6: all four controls render the design's TEXT
              glyphs (« ‹ › », `Fictiv Platform.dc.html:3787-3788`, one shared
              `navBtn` style). Previously the two YEAR buttons used text while
              the two MONTH buttons used Ant `LeftOutlined`/`RightOutlined`
              SVGs, so the set was sized by two different systems and could not
              look even in either direction. The glyphs are decorative — each
              button's `aria-label` is the accessible name, and must stay. */}
              <div className="date-range-picker-nav">
                <div className="date-range-picker-nav-group">
                  <button
                    type="button"
                    aria-label="Previous year"
                    className="date-range-picker-nav-button"
                    onClick={() => setAnchorMonth((current) => addMonths(current, -12))}
                  >
                    <span aria-hidden="true">«</span>
                  </button>
                  <button
                    type="button"
                    aria-label="Previous month"
                    className="date-range-picker-nav-button"
                    onClick={() => setAnchorMonth((current) => addMonths(current, -1))}
                  >
                    <span aria-hidden="true">‹</span>
                  </button>
                </div>
                <div className="date-range-picker-nav-group">
                  <button
                    type="button"
                    aria-label="Next month"
                    className="date-range-picker-nav-button"
                    onClick={() => setAnchorMonth((current) => addMonths(current, 1))}
                  >
                    <span aria-hidden="true">›</span>
                  </button>
                  <button
                    type="button"
                    aria-label="Next year"
                    className="date-range-picker-nav-button"
                    onClick={() => setAnchorMonth((current) => addMonths(current, 12))}
                  >
                    <span aria-hidden="true">»</span>
                  </button>
                </div>
              </div>

              <div className="date-range-picker-months">
                <Month model={leftMonth} onDayClick={handleDayClick} />
                <Month model={rightMonth} onDayClick={handleDayClick} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Month({
  model,
  onDayClick,
}: {
  readonly model: MonthModel
  readonly onDayClick: (date: Date) => void
}) {
  return (
    <div className="date-range-picker-month">
      <div className="date-range-picker-month-title">{model.title}</div>
      <div className="date-range-picker-weekdays" aria-hidden="true">
        {WEEKDAY_LABELS.map((weekday, index) => (
          // Fixed 7-column weekday header; index is a stable identity here.
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length static array
          <span key={index}>{weekday}</span>
        ))}
      </div>
      <div className="date-range-picker-days">
        {model.cells.map((cell) =>
          cell.inMonth ? (
            <button
              key={cell.date.toISOString()}
              type="button"
              className={
                cell.isEndpoint
                  ? 'date-range-picker-day date-range-picker-day-endpoint'
                  : cell.inRange
                    ? 'date-range-picker-day date-range-picker-day-in-range'
                    : 'date-range-picker-day'
              }
              aria-pressed={cell.isEndpoint}
              aria-label={cell.date.toLocaleDateString(undefined, {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
              onClick={() => onDayClick(cell.date)}
            >
              {cell.date.getDate()}
            </button>
          ) : (
            <span
              key={cell.date.toISOString()}
              className="date-range-picker-day-blank"
              aria-hidden="true"
            >
              {cell.date.getDate()}
            </span>
          ),
        )}
      </div>
    </div>
  )
}
